if (!customElements.get("m-icon-box-fade")) {
  class MIconboxFade extends HTMLElement {
    constructor() {
      super();
      this.perPageDesktop = parseInt(this.dataset.perPageDesktop, 10) || 4;
      this.perPageTablet = parseInt(this.dataset.perPageTablet, 10) || this.perPageDesktop;
      this.perPageMobile = parseInt(this.dataset.perPageMobile, 10) || 1;
      this.autoplayDelay = parseInt(this.dataset.autoplayDelay, 10) || 5000;
      this.transitionMs = parseInt(this.dataset.transitionMs, 10) || 600;
      this.breakpoint = parseInt(this.dataset.breakpoint, 10) || 768;
      this.breakpointDesktop = parseInt(this.dataset.breakpointDesktop, 10) || 1280;

      this.track = this.querySelector("[data-fade-track]");
      this.dotsContainer = this.querySelector("[data-fade-dots]");
      this.cards = Array.from(this.querySelectorAll("[data-fade-card]"));

      this.currentPage = 0;
      this.autoplayTimer = null;
      this._onVisibility = this._onVisibility.bind(this);
      this._onBreakpointChange = this._onBreakpointChange.bind(this);
      this._onTouchStart = this._onTouchStart.bind(this);
      this._onTouchMove = this._onTouchMove.bind(this);
      this._onTouchEnd = this._onTouchEnd.bind(this);
      this._touchStartX = 0;
      this._touchStartY = 0;
      this._touchDeltaX = 0;
      this._touchActive = false;
      this._touchLockedHorizontal = false;
    }

    connectedCallback() {
      if (!this.track || this.cards.length === 0) return;

      this.style.setProperty("--fade-duration", this.transitionMs + "ms");

      this.cards.forEach((card) => card.remove());

      this.mql = window.matchMedia("(min-width: " + this.breakpoint + "px)");
      this.mqlDesktop = window.matchMedia("(min-width: " + this.breakpointDesktop + "px)");
      if (this.mql.addEventListener) {
        this.mql.addEventListener("change", this._onBreakpointChange);
        this.mqlDesktop.addEventListener("change", this._onBreakpointChange);
      } else {
        this.mql.addListener(this._onBreakpointChange);
        this.mqlDesktop.addListener(this._onBreakpointChange);
      }
      document.addEventListener("visibilitychange", this._onVisibility);
      document.addEventListener("shopify:block:select", (e) => this._onBlockSelect(e));

      this.track.addEventListener("touchstart", this._onTouchStart, { passive: true });
      this.track.addEventListener("touchmove", this._onTouchMove, { passive: false });
      this.track.addEventListener("touchend", this._onTouchEnd);
      this.track.addEventListener("touchcancel", this._onTouchEnd);

      this.rebuild();
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      document.removeEventListener("visibilitychange", this._onVisibility);
      if (this.track) {
        this.track.removeEventListener("touchstart", this._onTouchStart);
        this.track.removeEventListener("touchmove", this._onTouchMove);
        this.track.removeEventListener("touchend", this._onTouchEnd);
        this.track.removeEventListener("touchcancel", this._onTouchEnd);
      }
      if (this.mql) {
        if (this.mql.removeEventListener) {
          this.mql.removeEventListener("change", this._onBreakpointChange);
        } else {
          this.mql.removeListener(this._onBreakpointChange);
        }
      }
      if (this.mqlDesktop) {
        if (this.mqlDesktop.removeEventListener) {
          this.mqlDesktop.removeEventListener("change", this._onBreakpointChange);
        } else {
          this.mqlDesktop.removeListener(this._onBreakpointChange);
        }
      }
    }

    _currentPerPage() {
      if (this.mqlDesktop && this.mqlDesktop.matches) return this.perPageDesktop;
      if (this.mql && this.mql.matches) return this.perPageTablet;
      return this.perPageMobile;
    }

    rebuild() {
      const perPage = this._currentPerPage();
      const pageCount = Math.ceil(this.cards.length / perPage);

      this.track.innerHTML = "";
      this.dotsContainer.innerHTML = "";

      this.pages = [];
      this.dots = [];

      for (let i = 0; i < pageCount; i++) {
        const page = document.createElement("div");
        page.className = "m-icon-box-fade__page";
        page.dataset.page = String(i);

        const grid = document.createElement("div");
        grid.className = "m-icon-box-fade__grid";
        grid.style.setProperty("--per-page", String(perPage));

        const start = i * perPage;
        const end = Math.min(start + perPage, this.cards.length);
        for (let j = start; j < end; j++) {
          grid.appendChild(this.cards[j]);
        }

        page.appendChild(grid);
        this.track.appendChild(page);
        this.pages.push(page);

        const dot = document.createElement("button");
        dot.type = "button";
        dot.className = "m-icon-box-fade__dot";
        dot.setAttribute("aria-label", "Go to slide " + (i + 1));
        dot.dataset.page = String(i);
        dot.addEventListener("click", () => this.goTo(i, true));
        this.dotsContainer.appendChild(dot);
        this.dots.push(dot);
      }

      if (this.currentPage >= pageCount) this.currentPage = 0;
      this._activate(this.currentPage);
    }

    _activate(idx) {
      this.pages.forEach((page, i) => {
        const isActive = i === idx;
        page.classList.toggle("is-active", isActive);
        page.setAttribute("aria-hidden", String(!isActive));
      });
      this.dots.forEach((dot, i) => {
        const isActive = i === idx;
        dot.classList.toggle("is-active", isActive);
        if (isActive) dot.setAttribute("aria-current", "true");
        else dot.removeAttribute("aria-current");
      });
      this.currentPage = idx;
    }

    goTo(idx, userInitiated) {
      if (idx < 0 || idx >= this.pages.length) return;
      this._activate(idx);
      if (userInitiated) this._restartAutoplay();
    }

    next() {
      if (!this.pages || this.pages.length <= 1) return;
      this._activate((this.currentPage + 1) % this.pages.length);
    }

    prev() {
      if (!this.pages || this.pages.length <= 1) return;
      this._activate((this.currentPage - 1 + this.pages.length) % this.pages.length);
    }

    _onTouchStart(e) {
      if (this.mql && this.mql.matches) return;
      if (!e.touches || e.touches.length !== 1) return;
      this._touchActive = true;
      this._touchLockedHorizontal = false;
      this._touchStartX = e.touches[0].clientX;
      this._touchStartY = e.touches[0].clientY;
      this._touchDeltaX = 0;
    }

    _onTouchMove(e) {
      if (!this._touchActive || !e.touches || e.touches.length !== 1) return;
      const dx = e.touches[0].clientX - this._touchStartX;
      const dy = e.touches[0].clientY - this._touchStartY;
      this._touchDeltaX = dx;
      if (!this._touchLockedHorizontal && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
        this._touchLockedHorizontal = Math.abs(dx) > Math.abs(dy);
      }
      if (this._touchLockedHorizontal && e.cancelable) {
        e.preventDefault();
      }
    }

    _onTouchEnd() {
      if (!this._touchActive) return;
      this._touchActive = false;
      this._touchLockedHorizontal = false;
      const threshold = 40;
      if (this._touchDeltaX <= -threshold) {
        this.next();
        this._restartAutoplay();
      } else if (this._touchDeltaX >= threshold) {
        this.prev();
        this._restartAutoplay();
      }
      this._touchDeltaX = 0;
    }

    startAutoplay() {
      if (!this.pages || this.pages.length <= 1) return;
      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => this.next(), this.autoplayDelay);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    _restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    _onVisibility() {
      if (document.hidden) this.stopAutoplay();
      else this.startAutoplay();
    }

    _onBreakpointChange() {
      this.rebuild();
      this._restartAutoplay();
    }

    _onBlockSelect(e) {
      const card = e.target && e.target.closest && e.target.closest("[data-fade-card]");
      if (!card) return;
      const cardIndex = parseInt(card.dataset.cardIndex, 10);
      if (Number.isNaN(cardIndex)) return;
      const perPage = this._currentPerPage();
      const pageIdx = Math.floor(cardIndex / perPage);
      this.goTo(pageIdx, true);
    }
  }

  customElements.define("m-icon-box-fade", MIconboxFade);
}
